import { useState, memo, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOutCardinal, X, EnvelopeSimple, WhatsappLogo } from '@phosphor-icons/react';

const statesGeoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const countiesGeoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json";

const stateFips = {
  'Alabama': '01', 'Alaska': '02', 'Arizona': '04', 'Arkansas': '05', 'California': '06',
  'Colorado': '08', 'Connecticut': '09', 'Delaware': '10', 'Florida': '12', 'Georgia': '13',
  'Hawaii': '15', 'Idaho': '16', 'Illinois': '17', 'Indiana': '18', 'Iowa': '19',
  'Kansas': '20', 'Kentucky': '21', 'Louisiana': '22', 'Maine': '23', 'Maryland': '24',
  'Massachusetts': '25', 'Michigan': '26', 'Minnesota': '27', 'Mississippi': '28', 'Missouri': '29',
  'Montana': '30', 'Nebraska': '31', 'Nevada': '32', 'New Hampshire': '33', 'New Jersey': '34',
  'New Mexico': '35', 'New York': '36', 'North Carolina': '37', 'North Dakota': '38', 'Ohio': '39',
  'Oklahoma': '40', 'Oregon': '41', 'Pennsylvania': '42', 'Rhode Island': '44', 'South Carolina': '45',
  'South Dakota': '46', 'Tennessee': '47', 'Texas': '48', 'Utah': '49', 'Vermont': '50',
  'Virginia': '51', 'Washington': '53', 'West Virginia': '54', 'Wisconsin': '55', 'Wyoming': '56'
};

const stateCenters = {
  'Florida': { coordinates: [-81.5, 27.6], zoom: 5 },
  'Texas': { coordinates: [-99.9, 31.9], zoom: 4 },
  'Louisiana': { coordinates: [-92.0, 31.0], zoom: 5.5 },
  'Georgia': { coordinates: [-83.5, 32.7], zoom: 5.5 },
  'Wisconsin': { coordinates: [-89.5, 44.5], zoom: 5.5 },
  'New York': { coordinates: [-75.5, 43.0], zoom: 5 },
  'North Carolina': { coordinates: [-79.0, 35.5], zoom: 5.5 },
  'Illinois': { coordinates: [-89.0, 40.0], zoom: 5 },
  'Ohio': { coordinates: [-82.5, 40.0], zoom: 5.5 },
  'Missouri': { coordinates: [-92.5, 38.5], zoom: 5 },
  'California': { coordinates: [-119.4, 37.2], zoom: 4.5 },
  'Arizona': { coordinates: [-111.0, 34.0], zoom: 5 },
  'Colorado': { coordinates: [-105.5, 39.0], zoom: 5 },
  'Pennsylvania': { coordinates: [-77.5, 41.0], zoom: 5.5 },
  'Michigan': { coordinates: [-85.0, 44.0], zoom: 5 },
  'Washington': { coordinates: [-120.5, 47.5], zoom: 5.5 },
};

// --- CALCULATE JOB DATA FROM DEC 1, 2019 ---
function calculateWorkingDays() {
  const start = new Date(2019, 11, 1); // Dec 1, 2019
  const now = new Date();
  let days = 0;
  const d = new Date(start);
  while (d <= now) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) days++;
    d.setDate(d.getDate() + 1);
  }
  return days;
}

// Survey type ratio per 50 jobs
const surveyRatio = {
  'ALTA/NSPS Survey': 1,
  'Topographic Survey': 5,
  'Tree Survey': 1,
  'Boundary Survey': 30,
  'As-Built Survey': 3,
  'Mortgage Survey': 5,
  'Subdivision Plat': 2,
  'Construction Survey': 1,
  'Utility Mapping': 1,
  'Other': 1,
};

function getSurveyBreakdown(totalJobs) {
  const result = {};
  const ratioTotal = 50;
  for (const [type, ratio] of Object.entries(surveyRatio)) {
    result[type] = Math.round((totalJobs * ratio) / ratioTotal);
  }
  return result;
}

// Distribute jobs among counties with slight weighting (first counties get more)
function distributeJobs(totalJobs, countyNames) {
  const allocated = {};
  const countyCount = countyNames.length;
  // 85% to listed counties, 15% to "Other Counties"
  const listedTotal = Math.round(totalJobs * 0.85);
  const otherTotal = totalJobs - listedTotal;
  // Weight: first counties get slightly more (diminishing)
  const weights = countyNames.map((_, i) => Math.max(1, countyCount - i * 0.3));
  const weightSum = weights.reduce((a, b) => a + b, 0);
  let assigned = 0;
  countyNames.forEach((name, i) => {
    const share = Math.round((weights[i] / weightSum) * listedTotal);
    allocated[name] = { count: share, breakdown: getSurveyBreakdown(share) };
    assigned += share;
  });
  // Fix rounding
  if (assigned !== listedTotal && countyNames.length > 0) {
    allocated[countyNames[0]].count += (listedTotal - assigned);
  }
  if (otherTotal > 0) {
    allocated['Other Counties'] = { count: otherTotal, breakdown: getSurveyBreakdown(otherTotal) };
  }
  return allocated;
}

// Primary state config
const primaryStates = {
  'Ohio': {
    dailyAvg: 40,
    counties: ['Franklin', 'Delaware', 'Licking', 'Fairfield', 'Pickaway', 'Morrow', 'Perry', 'Muskingum', 'Guernsey', 'Noble', 'Morgan', 'Coshocton', 'Stark', 'Holmes', 'Tuscarawas', 'Jefferson', 'Carroll', 'Harrison', 'Athens', 'Fayette', 'Highland', 'Hocking', 'Jackson', 'Madison', 'Meigs', 'Pike', 'Ross', 'Scioto', 'Vinton'],
  },
  'Missouri': {
    dailyAvg: 15,
    counties: ['St. Charles', 'Lincoln', 'Warren', 'Franklin', 'St. Louis', 'Calhoun', 'Jersey', 'Madison'],
  },
  'Florida': {
    dailyAvg: 10,
    counties: ['Broward', 'Miami-Dade', 'Palm Beach', 'Orange', 'Polk', 'Lee', 'Seminole', 'Sarasota', 'Lake', 'Manatee', 'Volusia', 'Osceola', 'Santa Rosa', 'Jackson', 'Madison', 'Jefferson', 'Washington'],
  },
  'Illinois': {
    dailyAvg: 10,
    counties: ['Will', 'Cook', 'DuPage', 'Lake', 'Kane', 'McHenry', 'Iroquois', 'Livingston', 'Tazewell'],
  },
  'Texas': {
    dailyAvg: 5,
    counties: ['Austin', 'Anderson', 'Harris', 'Harrison', 'Houston', 'Howard', 'Jefferson', 'Jackson', 'Orange', 'Moore', 'Morris', 'Dallas', 'Denton', 'Galveston'],
  },
  'Georgia': {
    dailyAvg: 3,
    counties: ['Gwinnett', 'DeKalb', 'Fulton', 'Forsyth', 'Hall', 'Barrow', 'Rockdale', 'Walton'],
  },
  'Louisiana': {
    dailyAvg: 2,
    counties: ['West Feliciana Parish', 'East Feliciana Parish', 'West Baton Rouge Parish', 'Iberville Parish', 'St. Martin Parish', 'St. Landry Parish', 'Avoyelles Parish'],
  },
  'Wisconsin': {
    dailyAvg: 2,
    counties: ['St. Croix', 'Pierce', 'Polk', 'Dunn', 'Barron'],
  },
  'North Carolina': {
    dailyAvg: 2,
    counties: ['Hyde', 'Dare', 'Carteret'],
  },
  'New York': {
    dailyAvg: 1,
    counties: ['Suffolk', 'Nassau', 'Babylon', 'Smithtown', 'Huntington'],
  },
};

// All 50 states list
const allStates = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
  'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
  'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

function computeAllStateData() {
  const workingDays = calculateWorkingDays();
  const monthsActive = Math.round((new Date() - new Date(2019, 11, 1)) / (1000 * 60 * 60 * 24 * 30.44));
  const data = {};

  // Primary states
  for (const [state, config] of Object.entries(primaryStates)) {
    const totalJobs = config.dailyAvg * workingDays;
    data[state] = {
      count: totalJobs,
      dailyAvg: config.dailyAvg,
      primary: true,
      breakdown: getSurveyBreakdown(totalJobs),
      counties: distributeJobs(totalJobs, config.counties),
    };
  }

  // Other states: random count between 240-330
  const otherStates = allStates.filter(s => !primaryStates[s]);
  // Seeded pseudo-random per state name for consistency
  for (const state of otherStates) {
    let hash = 0;
    for (let i = 0; i < state.length; i++) hash = ((hash << 5) - hash) + state.charCodeAt(i);
    const totalJobs = 240 + Math.abs(hash % 91); // 240 to 330
    data[state] = {
      count: totalJobs,
      dailyAvg: 0,
      primary: false,
      breakdown: getSurveyBreakdown(totalJobs),
      counties: {},
    };
  }

  return data;
}

// --- COMPONENT ---
const USMapComponent = ({ selectedType, onStateSelect }) => {
  const [position, setPosition] = useState({ coordinates: [-96, 38], zoom: 1 });
  const [hoveredState, setHoveredState] = useState(null);
  const [hoveredCounty, setHoveredCounty] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [showCounties, setShowCounties] = useState(false);

  const stateData = useMemo(() => computeAllStateData(), []);

  // Find max count for color scale
  const maxCount = useMemo(() => Math.max(...Object.values(stateData).map(d => d.count)), [stateData]);
  const typeRank = useMemo(() => {
    if (!selectedType) return {};
    const sorted = Object.entries(stateData).sort((a, b) => (a[1].breakdown[selectedType] || 0) - (b[1].breakdown[selectedType] || 0));
    return Object.fromEntries(sorted.map(([n], i) => [n, i / Math.max(sorted.length - 1, 1)]));
  }, [stateData, selectedType]);

  const handleZoomIn = () => { if (position.zoom < 8) setPosition(pos => ({ ...pos, zoom: pos.zoom * 1.5 })); };
  const handleZoomOut = () => {
    if (position.zoom <= 1) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom / 1.5 }));
    if (position.zoom <= 2) { setShowCounties(false); setSelectedState(null); }
  };
  const handleReset = () => { setPosition({ coordinates: [-96, 38], zoom: 1 }); setShowCounties(false); setSelectedState(null); setHoveredCounty(null); };
  const handleMoveEnd = (pos) => setPosition(pos);

  const handleStateClick = useCallback((geo) => {
    const name = geo.properties.name;
    setSelectedState(name);
    if (onStateSelect) onStateSelect(name);
    const center = stateCenters[name];
    if (center) { setPosition(center); setShowCounties(true); }
    else { setPosition(pos => ({ ...pos, zoom: 4 })); setShowCounties(true); }
  }, [onStateSelect]);

  const getStateColor = (geo) => {
    const name = geo.properties.name;
    const d = stateData[name];
    if (selectedState === name) return '#3B82F6';
    if (hoveredState === name) return '#8B5CF6';
    if (!d) return '#F4F4F5';
    if (selectedType) {
      const r = typeRank[name] ?? 0;
      return r > 0.75 ? '#2DD4BF' : r > 0.5 ? '#5EEAD4' : r > 0.25 ? '#99F6E4' : '#CCFBF1';
    }
    if (d.primary) {
      const intensity = Math.min(d.count / maxCount, 1);
      if (intensity > 0.6) return '#93C5FD'; // high
      if (intensity > 0.2) return '#BFDBFE'; // medium
      return '#DBEAFE'; // low primary
    }
    return '#EDE9FE'; // other states (light purple tint)
  };

  const getCountyColor = (geo) => {
    const countyId = geo.id;
    const stateCode = countyId?.toString().substring(0, 2);
    if (selectedState && stateFips[selectedState] === stateCode) {
      const countyName = geo.properties?.name;
      const sData = stateData[selectedState];
      if (sData?.counties?.[countyName]) {
        if (hoveredCounty === countyName) return '#8B5CF6';
        return '#93C5FD';
      }
      if (hoveredCounty === countyName) return '#C4B5FD';
      return '#DBEAFE';
    }
    return '#F4F4F5';
  };

  const activeState = hoveredState || selectedState;
  const activeData = activeState ? stateData[activeState] : null;

  return (
    <div className="relative" data-testid="us-map">
      {/* Map Controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2" data-testid="map-controls">
        <button onClick={handleZoomIn} className="w-10 h-10 bg-white border border-zinc-200 flex items-center justify-center hover:bg-[#F3F0EB] transition-colors rounded" data-testid="map-zoom-in"><MagnifyingGlassPlus size={20} /></button>
        <button onClick={handleZoomOut} className="w-10 h-10 bg-white border border-zinc-200 flex items-center justify-center hover:bg-[#F3F0EB] transition-colors rounded" data-testid="map-zoom-out"><MagnifyingGlassMinus size={20} /></button>
        <button onClick={handleReset} className="w-10 h-10 bg-white border border-zinc-200 flex items-center justify-center hover:bg-[#F3F0EB] transition-colors rounded" data-testid="map-reset"><ArrowsOutCardinal size={20} /></button>
      </div>

      {/* Info Panel */}
      {activeState && activeData && (
        <div className="absolute top-4 left-4 z-10 bg-white border border-zinc-200 p-4 max-w-xs shadow-lg rounded-md animate-fade-in" style={{ maxHeight: '80%', overflowY: 'auto' }} data-testid="map-tooltip">
          {selectedState && (
            <button onClick={handleReset} className="absolute top-2 right-2 p-1 hover:bg-zinc-100 transition-colors rounded"><X size={16} /></button>
          )}
          
          <h3 className="font-bold text-zinc-900 pr-6" style={{ fontFamily: 'Outfit' }}>
            {activeState}
          </h3>
          <p className="text-2xl font-black text-[#3B82F6] mt-1" style={{ fontFamily: 'Outfit' }}>
            {activeData.count.toLocaleString()}
          </p>
          <p className="text-xs text-zinc-500">total jobs since Dec 2019</p>
          {selectedType && <p className="text-sm font-semibold text-teal-600 mt-1" data-testid="map-type-count">{(activeData.breakdown[selectedType] || 0).toLocaleString()} {selectedType} jobs</p>}
          {activeData.primary && (
            <p className="text-xs text-zinc-400 mt-0.5">~{activeData.dailyAvg} jobs/day average</p>
          )}

          {/* Survey breakdown */}
          <div className="mt-3 space-y-1">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Survey Types</p>
            {Object.entries(activeData.breakdown).map(([type, count]) => (
              <div key={type} className={`flex justify-between text-xs ${type === selectedType ? 'bg-teal-50 font-semibold' : ''}`}>
                <span className="text-zinc-600">{type}</span>
                <span className="text-zinc-900 font-medium">{count.toLocaleString()}</span>
              </div>
            ))}
          </div>

          {/* Counties */}
          {showCounties && selectedState && Object.keys(activeData.counties).length > 0 && (
            <div className="mt-3 border-t border-zinc-200 pt-3">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono mb-2">Counties / Parishes</p>
              <div className="space-y-2">
                {Object.entries(activeData.counties)
                  .sort((a, b) => b[1].count - a[1].count)
                  .map(([county, cData]) => (
                  <div key={county} className="group">
                    <div className="flex justify-between items-center text-xs">
                      <span className={`font-medium ${county === hoveredCounty ? 'text-[#3B82F6]' : 'text-zinc-700'}`}>{county}</span>
                      <span className="text-zinc-500">{cData.count.toLocaleString()}</span>
                    </div>
                    {/* Request Example links */}
                    {county !== 'Other Counties' && (
                      <div className="flex items-center gap-2 mt-0.5 opacity-70 group-hover:opacity-100 transition-opacity">
                        <a
                          href={`mailto:enquiries@surveysmaps.com?subject=Request Example - ${county}, ${selectedState}&body=Hi, I'd like to see example work from ${county}, ${selectedState}.`}
                          className="inline-flex items-center gap-0.5 text-[10px] text-[#3B82F6] hover:underline"
                          data-testid={`example-email-${county}`}
                        >
                          <EnvelopeSimple size={10} /> Request Example
                        </a>
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-0.5 text-[10px] text-[#8B5CF6] hover:underline"
                        >
                          Contact
                        </Link>
                        <a
                          href={`https://wa.me/19383071829?text=${encodeURIComponent(`Hi, I'd like to see example work from ${county}, ${selectedState}.`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[10px] text-[#25D366] hover:underline"
                        >
                          <WhatsappLogo size={10} weight="fill" />
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!showCounties && activeData.primary && (
            <p className="text-xs text-[#3B82F6] mt-3 cursor-pointer">Click to view counties →</p>
          )}

          {/* Request Example for all states (shown when not drilling into counties OR state has no county data) */}
          {(!showCounties || Object.keys(activeData.counties || {}).length === 0) && (
            <div className="mt-3 border-t border-zinc-200 pt-3">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono mb-2">Request Examples</p>
              <div className="flex items-center gap-2">
                <a
                  href={`mailto:enquiries@surveysmaps.com?subject=Request Example - ${activeState}&body=Hi, I'd like to see example survey work from ${activeState}.`}
                  className="inline-flex items-center gap-1 text-xs text-[#3B82F6] hover:underline"
                >
                  <EnvelopeSimple size={12} /> Email
                </a>
                <Link to="/contact" className="inline-flex items-center gap-1 text-xs text-[#8B5CF6] hover:underline">
                  Contact Form
                </Link>
                <a
                  href={`https://wa.me/19383071829?text=${encodeURIComponent(`Hi, I'd like to see example survey work from ${activeState}.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#25D366] hover:underline"
                >
                  <WhatsappLogo size={12} weight="fill" /> WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Map */}
      <div className="us-map-container bg-[#F3F0EB] border border-zinc-200 overflow-hidden rounded-md" style={{ aspectRatio: '16/9' }}>
        <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: '100%' }}>
          <ZoomableGroup zoom={position.zoom} center={position.coordinates} onMoveEnd={handleMoveEnd} minZoom={1} maxZoom={8}>
            {showCounties && (
              <Geographies geography={countiesGeoUrl}>
                {({ geographies }) =>
                  geographies
                    .filter(geo => selectedState && (geo.id?.toString() || '').startsWith(stateFips[selectedState] || ''))
                    .map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => setHoveredCounty(geo.properties?.name)}
                        onMouseLeave={() => setHoveredCounty(null)}
                        style={{
                          default: { fill: getCountyColor(geo), stroke: '#A1A1AA', strokeWidth: 0.3, outline: 'none' },
                          hover: { fill: '#8B5CF6', stroke: '#3B82F6', strokeWidth: 0.5, outline: 'none', cursor: 'pointer' },
                        }}
                      />
                    ))
                }
              </Geographies>
            )}
            <Geographies geography={statesGeoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => !showCounties && setHoveredState(geo.properties.name)}
                    onMouseLeave={() => setHoveredState(null)}
                    onClick={() => handleStateClick(geo)}
                    style={{
                      default: {
                        fill: showCounties && selectedState === geo.properties.name ? 'transparent' : getStateColor(geo),
                        stroke: showCounties && selectedState === geo.properties.name ? '#3B82F6' : '#A1A1AA',
                        strokeWidth: showCounties && selectedState === geo.properties.name ? 1.5 : 0.5,
                        outline: 'none',
                      },
                      hover: {
                        fill: showCounties ? 'transparent' : '#8B5CF6',
                        stroke: '#3B82F6', strokeWidth: 1, outline: 'none', cursor: 'pointer',
                      },
                      pressed: { fill: '#3B82F6', stroke: '#2563EB', strokeWidth: 1, outline: 'none' },
                    }}
                    data-testid={`map-state-${geo.properties.name?.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                ))
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 justify-center text-xs" data-testid="map-legend">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#93C5FD] rounded-sm"></div><span className="text-zinc-600">High Volume</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#BFDBFE] rounded-sm"></div><span className="text-zinc-600">Medium Volume</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#DBEAFE] rounded-sm"></div><span className="text-zinc-600">Active Primary</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#EDE9FE] rounded-sm"></div><span className="text-zinc-600">Standard Coverage</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#3B82F6] rounded-sm"></div><span className="text-zinc-600">Selected</span></div>
      </div>
      
      <p className="text-center text-xs text-zinc-500 mt-2">
        {showCounties 
          ? `Viewing ${selectedState} counties. Click reset to view all states.`
          : 'Click on any state to view county-level job details. Serving all 50 states since December 2019.'
        }
      </p>
    </div>
  );
};

export const USMap = memo(USMapComponent);
